import { anchorNamesALine } from '@kk/domain';
import { Text, WasabiButton } from '@kk/ui';
import { useEffect, useRef, useState } from 'react';

import { useClients, useContentCan } from '@/auth/SessionProvider';
import { LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { useAddComment, useClaimReview, useComments, useErrorMessage, useLockMission, useResolveComment } from '@/data/hooks';
import type { AdminMission } from '@/types/wire';

const c = deck.o11;

/** Every line a comment can be pinned to, in reading order (@kk/domain `anchorNamesALine` decides which exist). */
function linesOf(m: AdminMission): string[] {
  const lines = ['title', 'concept', 'scripts.reader', 'scripts.prereader'];
  (m.beats ?? []).forEach((b, i) => {
    lines.push(`beats.${i}.prompt`, `beats.${i}.spoken`);
    for (const ch of (b.choices as Array<{ key: string }> | null | undefined) ?? []) lines.push(`beats.${i}.choices.${ch.key}.label`, `beats.${i}.choices.${ch.key}.consequence`);
  });
  m.quiz.forEach((_q, i) => lines.push(`quiz.${i}.prompt`));
  return lines.filter((l) => anchorNamesALine(l, m as never));
}

/**
 * **"{name} IS EDITING"** (Evan, 2026-09-26): one editor at a time. The lock frees after 15 minutes without typing; an
 * owner can take over sooner. Drawn only when the lock is someone else's.
 */
export function EditingBanner({ mission }: { mission: AdminMission }) {
  const owner = useContentCan('team');
  const lock = useLockMission();
  const errorMessage = useErrorMessage();
  const [error, setError] = useState<string | null>(null);
  if (!mission.editing || mission.editing.yours) return null;
  return (
    <div className="draft-banner" data-testid="editing-banner">
      <span>{c.editing(mission.editing.name)}</span>
      {owner ? (
        <LinkButton
          label={c.takeOver}
          onPress={() =>
            lock.mutateAsync({ id: mission.id, takeOver: true }).catch((e: unknown) => setError(errorMessage(e)))
          }
          testID="take-over"
        />
      ) : null}
      {error ? <Status>{error}</Status> : null}
    </div>
  );
}

/**
 * **The editor keeps its lock while someone types**, at most one call every 30 seconds, and lets go when they leave. A
 * sign-in that cannot edit never asks.
 */
export function useEditingLock(missionId: string, typing: unknown) {
  const mayEdit = useContentCan('edit');
  const lock = useLockMission();
  const { data } = useClients();
  const last = useRef(0);
  const key = JSON.stringify(typing ?? null);
  useEffect(() => {
    if (!mayEdit || typing === null) return;
    const now = Date.now();
    if (now - last.current < 30_000) return;
    last.current = now;
    lock.mutate({ id: missionId });
    // a heartbeat per change of the working copy (key), not per render
  }, [key]);
  useEffect(
    () => () => {
      if (mayEdit && last.current) void data.unlockMission(missionId).catch(() => undefined);
    },
    [data, missionId, mayEdit],
  );
}

/** **The first Publisher to open a waiting draft takes it**; the rest see who has. */
export function WaitingLine({ mission }: { mission: AdminMission }) {
  const mayPublish = useContentCan('publish');
  const claim = useClaimReview();
  const asked = useRef(false);
  useEffect(() => {
    if (!mayPublish || !mission.waiting || mission.waiting.claimedBy || asked.current) return;
    asked.current = true;
    claim.mutate(mission.id);
  }, [mayPublish, mission.waiting, mission.id, claim]);
  if (!mission.waiting?.claimedBy) return null;
  return (
    <Text variant="bodySm" tone="muted" testID="claimed-by">
      {c.claimedBy(mission.waiting.claimedBy)}
    </Text>
  );
}

/** **Comments on the draft, pinned to a line** (Evan, 2026-09-26). Anyone who may comment adds one or resolves one. */
export function CommentsPanel({ mission }: { mission: AdminMission }) {
  const mayComment = useContentCan('comment');
  const list = useComments(mission.id);
  const add = useAddComment(mission.id);
  const resolve = useResolveComment();
  const errorMessage = useErrorMessage();
  const lines = linesOf(mission);
  const [anchor, setAnchor] = useState(lines[2] ?? lines[0] ?? 'title');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const run = (fn: () => Promise<unknown>) => {
    setError(null);
    fn().catch((e: unknown) => setError(errorMessage(e)));
  };
  const comments = list.data ?? [];
  return (
    <section className="editor-card stack" data-testid="comments">
      <span className="eyebrow muted">{c.comments}</span>
      {comments.length === 0 ? (
        <Text variant="bodySm" tone="muted">
          {c.noComments}
        </Text>
      ) : (
        comments.map((cm) => (
          <div key={cm.id} className={`comment${cm.resolvedAt ? ' resolved' : ''}`} data-testid={`comment-${cm.id}`}>
            <Text variant="bodySm" tone="muted">
              {c.anchor(cm.anchor)} · {cm.name}
            </Text>
            <Text variant="body" tone="ink">
              {cm.text}
            </Text>
            {cm.resolvedAt ? (
              <Text variant="bodySm" tone="muted">
                {c.resolved}
              </Text>
            ) : mayComment ? (
              <LinkButton label={c.resolve} onPress={() => run(() => resolve.mutateAsync(cm.id))} testID={`resolve-${cm.id}`} />
            ) : null}
          </div>
        ))
      )}
      {mayComment ? (
        <div className="stack-tight" data-testid="comment-form">
          <label className="stack-tight">
            <Text variant="bodySm" tone="muted">
              {c.commentOn}
            </Text>
            <select className="team-date" value={anchor} onChange={(e) => setAnchor(e.target.value)} data-testid="comment-anchor">
              {lines.map((l) => (
                <option key={l} value={l}>
                  {c.anchor(l)}
                </option>
              ))}
            </select>
          </label>
          <textarea
            className="comment-text"
            aria-label={c.commentText}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="comment-text"
          />
          {text.trim() ? (
            <WasabiButton
              label={c.addComment}
              size="sm"
              fullWidth={false}
              onPress={() =>
                run(async () => {
                  await add.mutateAsync({ anchor, text: text.trim() });
                  setText('');
                })
              }
              testID="comment-add"
            />
          ) : null}
        </div>
      ) : null}
      {error ? <Status testID="comment-error">{error}</Status> : null}
    </section>
  );
}
